/**
 * EditInductionModal
 *
 * Modal form for editing an existing induction batch (log count, date, notes).
 * Adjusts the source purchase's logs_remaining when log_count changes.
 */

import { useState, useEffect } from 'react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Loader2 } from 'lucide-react'

export default function EditInductionModal({ open, onOpenChange, onSubmit, induction, purchases }) {
  const [formData, setFormData] = useState({
    log_count: '',
    induction_date: '',
    notes: ''
  })
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)

  const sourcePurchase = purchases.find(p => p.id === induction?.purchase_id)
  // Max logs = currently remaining + what this induction already uses
  const maxLogs = sourcePurchase
    ? sourcePurchase.logs_remaining + (induction?.log_count || 0)
    : 999

  useEffect(() => {
    if (induction) {
      setFormData({
        log_count: String(induction.log_count),
        induction_date: induction.induction_date,
        notes: induction.notes || ''
      })
      setError(null)
    }
  }, [induction])

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setSaving(true)
    setError(null)

    try {
      const logCount = parseInt(formData.log_count, 10)
      if (logCount > maxLogs) {
        throw new Error(`Only ${maxLogs} logs available from this purchase`)
      }

      await onSubmit(induction.id, {
        log_count: logCount,
        induction_date: formData.induction_date,
        notes: formData.notes || null
      })
      onOpenChange(false)
    } catch (err) {
      setError(err.message)
    } finally {
      setSaving(false)
    }
  }

  if (!induction) return null

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Edit Induction — {induction.induction_code}</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="edit_induction_date">Induction Date</Label>
              <Input
                id="edit_induction_date"
                type="date"
                value={formData.induction_date}
                onChange={(e) => handleChange('induction_date', e.target.value)}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit_log_count">
                Number of Logs
                <span className="text-muted-foreground font-normal"> (max {maxLogs})</span>
              </Label>
              <Input
                id="edit_log_count"
                type="number"
                min="1"
                max={maxLogs}
                value={formData.log_count}
                onChange={(e) => handleChange('log_count', e.target.value)}
                required
              />
            </div>
          </div>

          {sourcePurchase && formData.log_count && (
            <div className="rounded-lg bg-muted/50 p-3 text-sm space-y-1">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Total substrate weight</span>
                <span className="font-medium">
                  {(parseInt(formData.log_count) * induction.log_weight_kg).toFixed(1)} kg
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Log cost</span>
                <span className="font-medium">
                  £{(parseInt(formData.log_count) * Number(sourcePurchase.cost_per_log)).toFixed(2)}
                </span>
              </div>
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="edit_notes">Notes</Label>
            <Input
              id="edit_notes"
              value={formData.notes}
              onChange={(e) => handleChange('notes', e.target.value)}
              placeholder="Optional notes..."
            />
          </div>

          {error && (
            <p className="text-sm text-destructive">{error}</p>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={saving}>
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Save Changes
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
